import {
  createGrpcWebTransport,
  createPromiseClient,
} from "@bufbuild/connect-web";
import { getAccessToken, getDefaultApiV2Headers } from "@mirage/service-auth";
import { tagged } from "@mirage/service-logging";

import type {
  ConnectError,
  Interceptor,
  PromiseClient,
  StreamResponse,
  UnaryResponse,
} from "@bufbuild/connect-web";
import type { ServiceType } from "@bufbuild/protobuf";

export const API_V2_GRPC_WEB_BASE_URL =
  "https://api.dropboxapi.com/web-grpc/2/";

export interface APIv2GRPCWebError extends Error {
  dropboxRequestId?: string;
}

const logger = tagged("grpc/api-v2");

function getExtendedGRPCError(e: Error, res?: UnaryResponse | StreamResponse) {
  // Attempt to extract the Dropbox request ID from the response headers or error
  // metadata and place it explicitly on the error object.
  const dropboxRequestId =
    res?.header.get("x-dropbox-request-id") ||
    (e as ConnectError)?.metadata?.get("x-dropbox-request-id") ||
    undefined;

  const error = e as APIv2GRPCWebError;
  error.dropboxRequestId = dropboxRequestId;

  return error;
}

export const extendErrorInterceptor: Interceptor = (next) => async (req) => {
  try {
    const result = await next(req);
    if (result.stream) {
      return {
        ...result,
        read: async () => {
          try {
            return await result.read();
          } catch (e) {
            const error = getExtendedGRPCError(e as Error, result);
            throw error;
          }
        },
      };
    }
    return result;
  } catch (e) {
    const error = getExtendedGRPCError(e as Error);
    throw error;
  }
};

export const apiV2HeadersInterceptor: Interceptor = (next) => async (req) => {
  const accessToken = await getAccessToken();
  if (!accessToken) {
    throw new Error(
      "Access token is required to make API v2 requests. Please ensure the user is authenticated.",
    );
  }

  const allHeaders = await getDefaultApiV2Headers();
  allHeaders["Authorization"] = `Bearer ${accessToken}`;

  if (allHeaders["X-Dropbox-Validation"] === "val")
    logger.log("Using validation backend for gRPC API v2 requests");
  else if (allHeaders["X-Dropbox-Backend"] === "stage")
    logger.log("Using stage backend for gRPC API v2 requests");

  Object.entries({
    ...allHeaders,
  })
    .filter(([_, value]) => !!value)
    // if this is a custom header (starts with x-) and it was already set, don't override
    .filter(
      ([name, _]) =>
        !(name.toLowerCase().startsWith("x-") && req.header.has(name)),
    )
    .forEach(([name, value]) => {
      if (value && !req.header.has(name)) {
        req.header.set(name, value as string);
      }
    });
  return await next(req);
};

/**
 * Returns API v2 GRPC Web client.
 * @param service a service definition.
 * @param baseUrl an optional base url for requests.
 * @returns a promise client for making API v2 GRPC Web requests.
 */
export function createAPIv2GRPCWebPromiseClient<T extends ServiceType>(
  service: T,
  baseUrl: string = API_V2_GRPC_WEB_BASE_URL,
): PromiseClient<T> {
  const transport = createGrpcWebTransport({
    baseUrl,
    interceptors: [apiV2HeadersInterceptor, extendErrorInterceptor],
  });
  return createPromiseClient(service, transport);
}
